import {
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import { useLanguage } from '@treams/treams-hooks';
import {
	type TranscriptionLanguage,
	type TranscriptionTargetType,
	useCreateTranscriptionMutation,
	useFinalizeTranscriptionUploadMutation,
	useMarkTranscriptionUploadAsFailedMutation,
	usePrepareTranscriptionUploadRetryMutation,
} from '@treams/treams-network';

import { localeToTranscriptionLanguage } from './TranscriptionLanguageSelect';
import { useRecorder } from './hooks/useRecorder';

export type RecordingSession = {
	targetID: string;
	targetType: TranscriptionTargetType;
	label?: string;
	targetPath: string;
	onLinkTranscription: (transcriptionID: string) => Promise<void>;
	/** Pass `transcriptionID` after a failed S3 upload; omit for “unlink current” from UI. */
	onUnlinkTranscription: (transcriptionID?: string) => Promise<void>;
	onRefresh: () => void;
};

export type PendingUploadRetry = {
	transcriptionID: string;
	blob: Blob;
};

export enum RecordingState {
	IDLE = 'IDLE',
	COUNTDOWN = 'COUNTDOWN',
	RECORDING = 'RECORDING',
	UPLOADING = 'UPLOADING',
	PROCESSING = 'PROCESSING',
}

type UploadState = RecordingState.IDLE | RecordingState.UPLOADING | RecordingState.PROCESSING;

type GraphQLUploadHeader = readonly { name: string; values: readonly string[] }[];

function resolveRecordingState(
	uploadState: UploadState,
	recording: boolean,
	countdown: number | null,
): RecordingState {
	if (uploadState !== RecordingState.IDLE) return uploadState;
	if (recording) return RecordingState.RECORDING;
	if (countdown !== null) return RecordingState.COUNTDOWN;
	return RecordingState.IDLE;
}

function buildUploadHeaders(uploadHeaders: GraphQLUploadHeader): Headers {
	const headers = new Headers();
	for (const { name, values } of uploadHeaders) {
		for (const value of values) {
			headers.append(name, value);
		}
	}
	return headers;
}

function presignedPut(
	uploadURL: string,
	uploadHeaders: GraphQLUploadHeader,
	body: Blob,
): Promise<Response> {
	return fetch(uploadURL, {
		method: 'PUT',
		headers: buildUploadHeaders(uploadHeaders),
		body,
	});
}

function presignedPutFailureError(status: number, statusText: string): Error {
	return new Error(`Failed to upload recording (HTTP ${status} ${statusText})`);
}

type TranscriptionRecordingContextValue = {
	activeSession: RecordingSession | null;
	state: RecordingState;
	language: TranscriptionLanguage;
	setLanguage: (language: TranscriptionLanguage) => void;
	countdown: number | null;
	uploadError: Error | null;
	pendingRetry: PendingUploadRetry | null;
	retryUploadLoading: boolean;
	elapsed: number;
	bars: number[];
	current: number;
	startRecording: (session: RecordingSession) => void;
	stopRecording: () => void;
	cancelRecording: () => void;
	dismissError: () => void;
	retryUpload: () => Promise<void>;
	saveRecordingLocally: () => void;
	/** Resets upload/recording UI if this transcription was linked for the in-flight save/retry. */
	resetInFlightRecordingForTranscription: (transcriptionID: string) => void;
	isRecordingTarget: (targetID: string) => boolean;
};

const TranscriptionRecordingContext = createContext<TranscriptionRecordingContextValue | null>(
	null,
);

export function useTranscriptionRecordingContext() {
	const transcriptionRecordingContext = useContext(TranscriptionRecordingContext);

	if (!transcriptionRecordingContext) {
		throw new Error('useTranscriptionRecording must be used within TranscriptionRecordingProvider');
	}

	return transcriptionRecordingContext;
}

export function TranscriptionRecordingProvider({ children }: PropsWithChildren) {
	const { id: viewerLocale } = useLanguage();

	const [session, setSession] = useState<RecordingSession | null>(null);
	const [uploadState, setUploadState] = useState<UploadState>(RecordingState.IDLE);
	const [uploadError, setUploadError] = useState<Error | null>(null);
	const [pendingRetry, setPendingRetry] = useState<PendingUploadRetry | null>(null);
	const [retryUploadLoading, setRetryUploadLoading] = useState(false);
	const [language, setLanguage] = useState(() => localeToTranscriptionLanguage(viewerLocale));

	const sessionRef = useRef<RecordingSession | null>(null);
	const languageRef = useRef(language);
	const pendingRetryRef = useRef<PendingUploadRetry | null>(null);
	/** Set after link; used so unlinking that transcription can dismiss the upload/processing banner. */
	const inFlightTranscriptionIdRef = useRef<string | null>(null);

	/** Updates ref (for async readers) and React state (for UI). */
	const commitPendingRetry = useCallback((next: PendingUploadRetry | null) => {
		pendingRetryRef.current = next;
		setPendingRetry(next);
	}, []);

	const clearPendingRetryAndInFlight = useCallback(() => {
		commitPendingRetry(null);
		inFlightTranscriptionIdRef.current = null;
	}, [commitPendingRetry]);

	const [createTranscription] = useCreateTranscriptionMutation();
	const [finalizeUpload] = useFinalizeTranscriptionUploadMutation();
	const [markTranscriptionUploadAsFailed] = useMarkTranscriptionUploadAsFailedMutation();
	const [prepareTranscriptionUploadRetry] = usePrepareTranscriptionUploadRetryMutation();

	useEffect(() => {
		sessionRef.current = session;
	}, [session]);

	useEffect(() => {
		languageRef.current = language;
	}, [language]);

	const recoverFromFailedS3Put = useCallback(
		async (transcriptionID: string, currentSession: RecordingSession) => {
			try {
				await markTranscriptionUploadAsFailed({
					variables: { input: { transcriptionID } },
				});
			} catch {
				// best-effort — user still sees upload error below
			}

			try {
				currentSession.onRefresh();
			} catch {
				// best-effort
			}
		},
		[markTranscriptionUploadAsFailed],
	);

	const setIdleWithUploadError = useCallback((err: unknown) => {
		setUploadError(err instanceof Error ? err : new Error('Unknown error'));
		setUploadState(RecordingState.IDLE);
	}, []);

	const finalizeUploadAndCloseSession = useCallback(
		async (currentSession: RecordingSession, transcriptionID: string) => {
			setUploadState(RecordingState.PROCESSING);
			await finalizeUpload({
				variables: { input: { transcriptionID } },
			});
			currentSession.onRefresh();
			setUploadState(RecordingState.IDLE);
			clearPendingRetryAndInFlight();
			setSession(null);
		},
		[finalizeUpload, clearPendingRetryAndInFlight],
	);

	const handleRecordingSave = useCallback(
		async (blob: Blob) => {
			const currentSession = sessionRef.current;
			if (!currentSession) return;

			setUploadState(RecordingState.UPLOADING);
			setUploadError(null);
			clearPendingRetryAndInFlight();

			try {
				const result = await createTranscription({
					variables: {
						input: {
							contentLength: blob.size,
							contentType: blob.type,
							targetID: currentSession.targetID,
							targetType: currentSession.targetType,
							language: languageRef.current,
						},
					},
				});

				const transcriptionID = result.data?.createTranscription.transcription.id;
				const uploadURL = result.data?.createTranscription.uploadURL;
				const uploadHeaders = result.data?.createTranscription.uploadHeaders;

				if (!transcriptionID || !uploadURL || !uploadHeaders) {
					throw new Error('Failed to create transcription');
				}

				await currentSession.onLinkTranscription(transcriptionID);
				inFlightTranscriptionIdRef.current = transcriptionID;

				const uploadResponse = await presignedPut(uploadURL, uploadHeaders, blob);

				if (!uploadResponse.ok) {
					await recoverFromFailedS3Put(transcriptionID, currentSession);
					commitPendingRetry({ transcriptionID, blob });
					throw presignedPutFailureError(uploadResponse.status, uploadResponse.statusText);
				}

				await finalizeUploadAndCloseSession(currentSession, transcriptionID);
			} catch (err) {
				setIdleWithUploadError(err);
			}
		},
		[
			clearPendingRetryAndInFlight,
			commitPendingRetry,
			createTranscription,
			finalizeUploadAndCloseSession,
			recoverFromFailedS3Put,
			setIdleWithUploadError,
		],
	);

	const retryUpload = useCallback(async () => {
		const pending = pendingRetryRef.current;
		const currentSession = sessionRef.current;
		if (!pending || !currentSession) return;

		setRetryUploadLoading(true);
		setUploadError(null);
		setUploadState(RecordingState.UPLOADING);

		try {
			const prep = await prepareTranscriptionUploadRetry({
				variables: { input: { transcriptionID: pending.transcriptionID } },
			});

			const uploadURL = prep.data?.prepareTranscriptionUploadRetry.uploadURL;
			const uploadHeaders = prep.data?.prepareTranscriptionUploadRetry.uploadHeaders;
			const transcriptionID = prep.data?.prepareTranscriptionUploadRetry.transcriptionID;

			if (!transcriptionID || !uploadURL || !uploadHeaders) {
				throw new Error('Failed to prepare retry upload');
			}

			const uploadResponse = await presignedPut(uploadURL, uploadHeaders, pending.blob);

			if (!uploadResponse.ok) {
				await recoverFromFailedS3Put(transcriptionID, currentSession);
				throw presignedPutFailureError(uploadResponse.status, uploadResponse.statusText);
			}

			await finalizeUploadAndCloseSession(currentSession, transcriptionID);
		} catch (err) {
			setIdleWithUploadError(err);
		} finally {
			setRetryUploadLoading(false);
		}
	}, [
		finalizeUploadAndCloseSession,
		prepareTranscriptionUploadRetry,
		recoverFromFailedS3Put,
		setIdleWithUploadError,
	]);

	const saveRecordingLocally = useCallback(() => {
		const pending = pendingRetryRef.current;

		if (!pending) return;

		const mime = pending.blob.type || '';
		const extFromMime = mime.includes('/') ? mime.split('/')[1]?.split(/[;+]/)[0] : undefined;
		const ext =
			extFromMime && /^[a-z0-9]+$/i.test(extFromMime) ? extFromMime.toLowerCase() : 'webm';

		const objectUrl = URL.createObjectURL(pending.blob);
		const anchor = document.createElement('a');
		anchor.href = objectUrl;
		anchor.download = `treams-recording-${pending.transcriptionID}.${ext}`;
		anchor.style.position = 'absolute';
		anchor.style.pointerEvents = 'none';
		anchor.style.opacity = '0';
		document.body.appendChild(anchor);
		anchor.click();
		setTimeout(() => {
			anchor.remove();
			URL.revokeObjectURL(objectUrl);
		}, 0);
	}, []);

	const {
		recording,
		countdown,
		elapsed,
		bars,
		current,
		recorderElement,
		requestMicrophone,
		stopRecording,
		cancelRecording: recorderCancel,
	} = useRecorder(handleRecordingSave);

	const state = resolveRecordingState(uploadState, recording, countdown);

	const shouldWarnBeforeUnload =
		(state !== RecordingState.IDLE && state !== RecordingState.PROCESSING) || !!pendingRetry;

	useEffect(() => {
		if (!shouldWarnBeforeUnload) return;

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [shouldWarnBeforeUnload]);

	const startRecording = useCallback(
		async (newSession: RecordingSession) => {
			if (state !== RecordingState.IDLE) return;

			setSession(newSession);
			setLanguage(localeToTranscriptionLanguage(viewerLocale));
			setUploadError(null);
			clearPendingRetryAndInFlight();

			const success = await requestMicrophone();

			if (!success) setSession(null);
		},
		[state, viewerLocale, requestMicrophone, clearPendingRetryAndInFlight],
	);

	const cancelRecording = useCallback(() => {
		recorderCancel();
		inFlightTranscriptionIdRef.current = null;
		setSession(null);
	}, [recorderCancel]);

	const dismissError = useCallback(() => {
		setUploadError(null);
		clearPendingRetryAndInFlight();
		setSession(null);
	}, [clearPendingRetryAndInFlight]);

	const resetInFlightRecordingForTranscription = useCallback(
		(transcriptionID: string) => {
			if (inFlightTranscriptionIdRef.current !== transcriptionID) return;
			setUploadState(RecordingState.IDLE);
			setUploadError(null);
			clearPendingRetryAndInFlight();
			setRetryUploadLoading(false);
			setSession(null);
		},
		[clearPendingRetryAndInFlight],
	);

	const isRecordingTarget = useCallback(
		(targetID: string) => !!session && session.targetID === targetID,
		[session],
	);

	const value: TranscriptionRecordingContextValue = {
		activeSession: session,
		state,
		language,
		setLanguage,
		countdown,
		uploadError,
		pendingRetry,
		retryUploadLoading,
		elapsed,
		bars,
		current,
		startRecording,
		stopRecording,
		cancelRecording,
		dismissError,
		retryUpload,
		saveRecordingLocally,
		resetInFlightRecordingForTranscription,
		isRecordingTarget,
	};

	return (
		<TranscriptionRecordingContext.Provider value={value}>
			{recorderElement}
			{children}
		</TranscriptionRecordingContext.Provider>
	);
}
